import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import FunctionalButton from "components/layout/FunctionalButton";
import Portal from "components/layout/Portal";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import { ReactComponent as PencilIcon } from "assets/svgs/Pencil.svg";
import { ReactComponent as UndoIcon } from "assets/svgs/Editor/undo.svg";
import ActionList from "./ActionList";
import ActionButtons from "./ActionButtons";
import WaitDialog from "./WaitDialog";
import ArmMissionDialog, {
  type ArmMissionChoice,
  type ArmPresetCommand,
} from "./ArmMissionDialog";
import PressElevatorButtonDialog, {
  type PressElevatorButtonArgs,
} from "./PressElevatorButtonDialog";
import LoadMapDialog from "./LoadMapDialog";
import StairTypeDialog from "components/control/StairTypeDialog";
import TakePhotoDialog from "./TakePhotoDialog";
import {
  Record360AppendStopDialog,
  Record360StopDownloadDialog,
} from "./Record360Dialog";
import { DockingRequiredConfirmDialog } from "./DockingMissionDialogs";
import FrontPhotoWaypointConfirmDialog from "./FrontPhotoWaypointConfirmDialog";
import StartHereConfirmDialog from "./StartHereConfirmDialog";
import PayloadHealthWarningDialog from "./PayloadHealthWarningDialog";
import { usePayloadHealthMissionStart } from "lib/mission/usePayloadHealthMissionStart";
import { missionStartStepIndexForActionId } from "lib/mission/payloadHealthCheck";
import SaveCurrentMapNameDialog from "./SaveCurrentMapNameDialog";
import { type LoadMapFilterSelection } from "./ActionRow";
import { useMissionControl } from "providers/navigation/MissionProvider";
import { useStairs } from "providers/StairsProvider";
import {
  DEFAULT_DOCKING_MISSION_ARGS,
  findMissionEndDockAction,
  hasPrependedUndockBeforeLoadMap,
  MISSION_END_DOCK_NOTE,
  missionHasDockingRequired,
  PREPENDED_LOAD_MAP_NOTE,
} from "lib/mission/dockingMissionSteps";
import {
  findMissionEndStop360Action,
  MISSION_END_STOP_360_NOTE,
  missionEndStop360SequenceOrder,
  nextRecord360Command,
} from "lib/mission/record360MissionSteps";
import {
  findPriorNavigateToPose,
  isFrontCameraName,
  shouldPromptWaypointBeforeFrontPhoto,
} from "lib/mission/frontPhotoWaypointGuard";
import { setMapUpdatePublish } from "lib/protocols/mapLoad";
import { ConnectionState } from "lib/consts/ARC/connection";
import { RootState } from "store";
import {
  setCurrentMission,
  clearMissionCreation,
  setIsCreating,
  setEditMode as dispatchSetEditMode,
} from "store/missionCreationSlice";
import {
  useGetMissionsQuery,
  useCreateMissionMutation,
  useUpdateMissionMutation,
  useGetMissionQuery
} from "../../graphql/missions.generated";
import {
  useGetActionsQuery,
  useCreateActionMutation,
  useUpdateActionMutation,
  useReorderActionMutation,
  useDeleteActionMutation,
  usePrependUndockBeforeLoadMapMutation,
} from "app/services/missionStorageApiEnhanced";
import { Action } from "app/services/types.generated";
import { useMap } from "providers/navigation/MapProvider";
import { useMapManager } from "providers/navigation/MapManagerProvider";
import { useToast } from "providers/ToastProvider";
import {
  fetchMissionStorageMaps,
  findMapIdByStcmStem,
  missionMapPickerDisplayLabel,
  missionMapStorageBaseName,
  stcmRelativePathForLoadMapPickerRow,
  type MissionStorageLoadMapPickerRow,
} from "lib/mission/missionStorageMaps";
import { useARC } from "providers/ARC/ARCProvider";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import {
  useGetBehaviourMissionProfileQuery,
  useGetBehaviourProfilesQuery,
  useSetBehaviourMissionProfileMutation,
} from "app/services/behaviourRulesApi";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";

import type { CreateMissionWizardPrefill } from "./CreateMissionWizardModal";

interface CreateUpdateMissionSubpageProps {
  onBack: () => void;
  /** Applied when opening create flow from the mission wizard (kept until leave). */
  wizardPrefill?: CreateMissionWizardPrefill | null;
  /** @deprecated Prefill is no longer cleared on apply; parent clears on leave. */
  onWizardPrefillConsumed?: () => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'retrying' | 'failed';
type MapStartMode = "unset" | "new" | "existing" | "legacy" | "current";
const SENTRY_CONFIG_ACTION_TYPE = "SentryModeConfig";
const SENTRY_CONFIG_ACTION_NAME = "Sentry Mode Between Waypoints";
/** GraphQL `Int` — use 0 for “insert at front” (see mission_storage reorder_action). */
const REORDER_TO_FIRST = 0;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

type ActionSnapshot = {
  missionId: string;
  actionType: string;
  name: string;
  note: string;
  args: unknown;
  sequenceOrder: number;
};

type ActionUndoEntry =
  | { type: "added"; actionId: string }
  | { type: "removed"; snapshot: ActionSnapshot };

const toActionSnapshot = (action: Action): ActionSnapshot => ({
  missionId: action.missionId,
  actionType: action.actionType,
  name: action.name,
  note: action.note || "",
  args: action.args,
  sequenceOrder: action.sequenceOrder,
});

const CreateUpdateMissionSubpage: React.FC<CreateUpdateMissionSubpageProps> = ({
  onBack,
  wizardPrefill = null,
}) => {
  const { t } = useTranslation(["missions", "common"]);
  const dispatch = useDispatch();
  const currentMissionId = useSelector((state: RootState) => state.missionCreation.currentMissionId);
  const isCreating = useSelector((state: RootState) => state.missionCreation.isCreating);
  const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);

  const [missionName, setMissionName] = useState("");
  const [nameError, setNameError] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [showStairDialog, setShowStairDialog] = useState(false);
  const [showLoadMapDialog, setShowLoadMapDialog] = useState(false);
  const [showTakePhotoDialog, setShowTakePhotoDialog] = useState(false);
  const [showRecord360AppendStop, setShowRecord360AppendStop] = useState(false);
  const [showRecord360StopDownload, setShowRecord360StopDownload] = useState(false);
  const record360StopNoteRef = useRef("");
  const [showFrontPhotoWaypointConfirm, setShowFrontPhotoWaypointConfirm] = useState(false);
  const [showCurrentMapNameDialog, setShowCurrentMapNameDialog] = useState(false);
  const [currentMapSaveBusy, setCurrentMapSaveBusy] = useState(false);
  const [pendingTakePhotoArgs, setPendingTakePhotoArgs] = useState<{
    camera_name: string;
    ptz_yaw: number;
    ptz_pitch: number;
  } | null>(null);
  const [showWaitDialog, setShowWaitDialog] = useState(false);
  const [showArmMissionDialog, setShowArmMissionDialog] = useState(false);
  const [showPressElevatorDialog, setShowPressElevatorDialog] = useState(false);
  const [showDockingRequiredConfirm, setShowDockingRequiredConfirm] = useState(false);
  const [actionCreateBusy, setActionCreateBusy] = useState(false);
  const actionCreateBusyRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [showDiscardOption, setShowDiscardOption] = useState(false);
  const [sentryModeBetweenWaypoints, setSentryModeBetweenWaypoints] = useState(false);
  // Edit chrome lives in redux so the map view can gate mission-waypoint pin editing.
  const editMode = useSelector((state: RootState) => state.missionCreation.editMode);
  const setEditMode = useCallback(
    (next: boolean) => dispatch(dispatchSetEditMode(next)),
    [dispatch]
  );
  const [startHereMode, setStartHereMode] = useState(false);
  const [startHerePending, setStartHerePending] = useState<{
    action: Action;
    index: number;
  } | null>(null);
  const [insertionIndex, setInsertionIndex] = useState<number | null>(null);
  /** After placing at the insertion point, the point follows the item just added. */
  const advanceInsertionIndex = useCallback((count: number) => {
    setInsertionIndex((prev) => (prev === null ? null : prev + count));
  }, []);
  // Initialize from wizard prefill so a remount (StrictMode / PagePush) cannot lose
  // mapStartReady after the parent one-shot-clears wizardPrefill.
  const [mapStartMode, setMapStartMode] = useState<MapStartMode>(
    () => wizardPrefill?.mapStartMode ?? "unset"
  );
  const [initialExistingMapDone, setInitialExistingMapDone] = useState(
    () => wizardPrefill?.initialExistingMapDone ?? false
  );
  const [missionSaveBusy, setMissionSaveBusy] = useState(false);
  const [actionUndoStack, setActionUndoStack] = useState<ActionUndoEntry[]>([]);
  const [scrollToActionId, setScrollToActionId] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const loadMapIntentRef = useRef<"manual" | "initial">("manual");
  /** Highest allocated display index per action type; survives list refetch lag. */
  const nextNameIndexRef = useRef<Record<string, number>>({});
  const actionsRef = useRef<Action[]>([]);

  const { setMissionRecording, setMissionRecordCallback } = useMissionControl();
  const { handleStairConfirm } = useStairs();
  const { robotPose } = useMap();
  const { saveMap: saveMapToDevice, clearMap, loadStcmFromRelativePath } = useMapManager();
  const { addToast } = useToast();
  const { selectedARC, controlSocket, connectionState, sendCommand } = useARC();
  const {
    isExecuting,
    currentMissionId: executingMissionId,
    executorState,
    executionResult,
    executeMission,
    cancelMission,
    pauseMission,
    resumeMission,
    clearExecutionDisplay,
    behaviourActionFeedback,
    stepStatuses,
  } = useMissionExecution();
  const {
    requestMissionStart,
    payloadWarningVisible,
    payloadWarningIssues,
    continueDespitePayloadWarning,
    cancelPayloadWarning,
  } = usePayloadHealthMissionStart();

  const [createMission] = useCreateMissionMutation();
  const [updateMission] = useUpdateMissionMutation();
  const [createAction] = useCreateActionMutation();
  const [updateAction] = useUpdateActionMutation();
  const [reorderAction] = useReorderActionMutation();
  const [deleteAction] = useDeleteActionMutation();
  const [prependUndockBeforeLoadMap] = usePrependUndockBeforeLoadMapMutation();
  const { data: missionsData } = useGetMissionsQuery();

  // Fetch mission details to get name
  const { data: missionData } = useGetMissionQuery(
    { id: currentMissionId || '' },
    { skip: !currentMissionId }
  );

  const { data: actionsData } = useGetActionsQuery(
    { missionId: currentMissionId || '' },
    { skip: !currentMissionId }
  );

  const { data: profilesData } = useGetBehaviourProfilesQuery(undefined, {
    skip: isCreating || !currentMissionId,
  });
  const { data: missionProfileData, refetch: refetchMissionProfile } = useGetBehaviourMissionProfileQuery(
    currentMissionId || "",
    { skip: isCreating || !currentMissionId }
  );
  const [setBehaviourMissionProfile, { isLoading: isSavingProfile }] = useSetBehaviourMissionProfileMutation();

  const actions = (actionsData?.actions || []) as Action[];
  actionsRef.current = actions;
  const visibleActions = actions.filter(a => a.actionType !== SENTRY_CONFIG_ACTION_TYPE);
  const sentryConfigAction = actions.find(a => a.actionType === SENTRY_CONFIG_ACTION_TYPE);
  const canUndoAction = actionUndoStack.length > 0;
  const showActionEditing = isCreating || editMode;
  const profiles = profilesData?.profiles ?? [];
  const assignedProfileId = missionProfileData?.assignment?.profile_id ?? "";
  const assignedProfileName =
    profiles.find((profile) => profile.id === assignedProfileId)?.name ?? t("common:notAssigned");

  const TERMINAL_MISSION_STATUSES = useMemo(
    () => new Set(["idle", "completed", "failed", "cancelled", "canceled", "succeeded"]),
    []
  );
  const normalizedExecutorStatus = String(executorState?.status || "").trim().toLowerCase();
  const executorMissionId = String(executorState?.mission_id || "").trim();
  const isExecutorDrivingThisMission =
    !isCreating &&
    Boolean(currentMissionId) &&
    executorMissionId === currentMissionId &&
    !TERMINAL_MISSION_STATUSES.has(normalizedExecutorStatus);
  const isThisMissionExecuting =
    !isCreating &&
    Boolean(currentMissionId) &&
    ((isExecuting && executingMissionId === currentMissionId) || isExecutorDrivingThisMission);

  const executingActionId = useMemo(() => {
    const activeEntry = Object.entries(stepStatuses).find(
      ([, status]) => status.status === "running" || status.status === "paused",
    );
    if (activeEntry) return activeEntry[0];
    if (!isThisMissionExecuting || !executorState) return null;
    const stepIndex = executorState.current_step_index;
    if (stepIndex >= 0 && stepIndex < visibleActions.length) {
      return visibleActions[stepIndex].id;
    }
    return null;
  }, [stepStatuses, isThisMissionExecuting, executorState, visibleActions]);

  const getExecutionStatusText = () => {
    if (!isThisMissionExecuting) return null;
    if (normalizedExecutorStatus === "running") {
      const currentStep = executorState?.current_step_index ?? -1;
      const totalSteps = executorState?.total_steps ?? visibleActions.length;
      if (currentStep >= 0) {
        return t("subpage.runningStep", { current: currentStep + 1, total: totalSteps });
      }
      return t("subpage.starting");
    }
    if (normalizedExecutorStatus === "paused") {
      const currentStep = executorState?.current_step_index ?? -1;
      const totalSteps = executorState?.total_steps ?? visibleActions.length;
      if (currentStep >= 0) {
        return t("subpage.pausedStep", { current: currentStep + 1, total: totalSteps });
      }
      return t("subpage.paused");
    }
    if (normalizedExecutorStatus === "returning") {
      return t("subpage.returningToLaunch");
    }
    return normalizedExecutorStatus || t("subpage.running");
  };

  const handleProfileChange = async (profileId: string) => {
    if (isCreating || !currentMissionId || !profileId) return;
    await setBehaviourMissionProfile({ missionId: currentMissionId, profileId }).unwrap();
    await refetchMissionProfile();
  };

  const maxIndexedName = (actionType: string, namePrefix: string, list: Action[]) => {
    const escapedPrefix = namePrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^${escapedPrefix}\\s+(\\d+)`);
    let max = 0;
    for (const action of list) {
      if (action.actionType !== actionType) continue;
      const match = action.name.match(re);
      if (match) max = Math.max(max, Number(match[1]));
    }
    return max;
  };

  const allocateActionName = (actionType: string, namePrefix: string) => {
    const fromList = maxIndexedName(actionType, namePrefix, actionsRef.current);
    const reserved = nextNameIndexRef.current[actionType] ?? 0;
    const next = Math.max(fromList, reserved) + 1;
    nextNameIndexRef.current[actionType] = next;
    return `${namePrefix} ${next}`;
  };

  const pushUndoAdded = useCallback((actionId: string, scrollIntoView = true) => {
    setActionUndoStack((prev) => [...prev, { type: "added", actionId }]);
    // Only scroll when appending at the bottom; mid-list "+" inserts should keep viewport.
    if (scrollIntoView) {
      setScrollToActionId(actionId);
    }
  }, []);

  const pushUndoRemoved = useCallback((action: Action) => {
    setActionUndoStack((prev) => [...prev, { type: "removed", snapshot: toActionSnapshot(action) }]);
  }, []);

  useEffect(() => {
    setActionUndoStack([]);
    setScrollToActionId(null);
    nextNameIndexRef.current = {};
  }, [currentMissionId]);

  useEffect(() => {
    mountedRef.current = true;
    actionCreateBusyRef.current = false;
    setActionCreateBusy(false);
    return () => { mountedRef.current = false; };
  }, []);

  // Remount / reopen with a new mission must not inherit a stuck create mutex.
  useEffect(() => {
    actionCreateBusyRef.current = false;
    setActionCreateBusy(false);
    mountedRef.current = true;
  }, [currentMissionId]);

  const executeWithRetry = useCallback(async (fn: () => Promise<unknown>, attempts = 3) => {
    if (!mountedRef.current) return;
    setSaveStatus('saving');
    for (let remaining = attempts; remaining > 0; remaining--) {
      try {
        await fn();
        if (mountedRef.current) setSaveStatus('saved');
        return;
      } catch (err) {
        if (!mountedRef.current) return;
        if (remaining > 1) {
          setSaveStatus('retrying');
          await delay(1000);
        } else {
          setSaveStatus('failed');
          console.error("Mutation failed after retries", err);
          throw err;
        }
      }
    }
  }, []);

  const runActionCreate = useCallback(async (fn: () => Promise<unknown>) => {
    if (actionCreateBusyRef.current || !mountedRef.current) return;
    actionCreateBusyRef.current = true;
    setActionCreateBusy(true);
    try {
      await executeWithRetry(fn);
    } finally {
      actionCreateBusyRef.current = false;
      setActionCreateBusy(false);
    }
  }, [executeWithRetry]);

  useEffect(() => {
    if (saveStatus === 'saved') {
      setShowDiscardOption(false);
    }
  }, [saveStatus]);

  useEffect(() => {
    const enabled = Boolean(sentryConfigAction?.args?.enabled);
    setSentryModeBetweenWaypoints(enabled);
  }, [sentryConfigAction]);

  // Update name from fetch
  useEffect(() => {
    if (missionData?.mission?.name) {
      setMissionName(missionData.mission.name);
    }
  }, [missionData]);

  useEffect(() => {
    if (!isCreating) {
      setMapStartMode("unset");
      setInitialExistingMapDone(false);
      setMissionSaveBusy(false);
      return;
    }
    // Creating a mission should start ready to add steps.
    setEditMode(true);
  }, [isCreating]);

  // Leave edit mode when this mission starts running so +/- / action chrome disappear.
  useEffect(() => {
    if (!isThisMissionExecuting) return;
    setEditMode(false);
    setInsertionIndex(null);
    setStartHereMode(false);
    setStartHerePending(null);
  }, [isThisMissionExecuting]);

  useEffect(() => {
    if (!wizardPrefill || !isCreating) return;
    setMapStartMode(wizardPrefill.mapStartMode);
    setInitialExistingMapDone(wizardPrefill.initialExistingMapDone);
    // Do not clear parent prefill here: one-shot clear races StrictMode / PagePush
    // remounts and leaves mapStartMode stuck at "unset" (Create Waypoint disabled).
    // Parent clears on back / open-other-mission / discard.
  }, [wizardPrefill, isCreating]);

  const normalizeMissionName = (name: string) => name.trim().toLowerCase();

  const isDuplicateMissionName = (candidateName: string) => {
    const normalizedCandidate = normalizeMissionName(candidateName);
    return (missionsData?.missions || []).some((mission) => (
      mission.id !== currentMissionId &&
      normalizeMissionName(mission.name) === normalizedCandidate
    ));
  };

  const resolveMapIdAfterDeviceSave = async (baseName: string): Promise<string | null> => {
    for (let attempt = 0; attempt < 15; attempt++) {
      try {
        const maps = await fetchMissionStorageMaps(arcProxyHost, arcId);
        const id = findMapIdByStcmStem(maps, baseName);
        if (id) return id;
      } catch {
        /* maps query may be unavailable; retry */
      }
      await delay(350 + attempt * 120);
    }
    return null;
  };

  const handleSaveMission = async () => {
    if (!isCreating || !currentMissionId || missionSaveBusy) return;

    if (mapStartMode === "new") {
      let baseName: string;
      try {
        baseName = missionMapStorageBaseName(missionName);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Invalid map name";
        addToast({
          type: "error",
          message: t("createEdit.invalidMapName", { message: msg }),
        });
        return;
      }

      setMissionSaveBusy(true);
      setSaveStatus("saving");
      try {
        await saveMapToDevice(baseName, "");
      } catch (e) {
        console.error("handleSaveMission: device save failed", e);
        if (mountedRef.current) setSaveStatus("failed");
        addToast({
          type: "error",
          message: t("createEdit.mapSaveFailed"),
        });
        setMissionSaveBusy(false);
        return;
      }

      const mapId = await resolveMapIdAfterDeviceSave(baseName);
      if (!mapId) {
        addToast({
          type: "warning",
          message: t("createEdit.mapSavedWarning"),
        });
        if (mountedRef.current) setSaveStatus("saved");
        setMissionSaveBusy(false);
        return;
      }

      try {
        const existingPrepended = actions.find(
          (a) => a.actionType === "LoadMap" && (a.note || "") === PREPENDED_LOAD_MAP_NOTE
        );

        if (existingPrepended) {
          await updateAction({
            id: existingPrepended.id,
            input: {
              name: `Load Map: ${missionMapPickerDisplayLabel(missionName)}`,
              note: PREPENDED_LOAD_MAP_NOTE,
              args: { map_id: mapId },
            },
          }).unwrap();
        } else {
          const hadOtherSteps = visibleActions.length > 0;
          const res = await createAction({
            input: {
              missionId: currentMissionId,
              actionType: "LoadMap",
              name: `Load Map: ${missionMapPickerDisplayLabel(missionName)}`,
              note: PREPENDED_LOAD_MAP_NOTE,
              args: { map_id: mapId },
              sequenceOrder: 0,
            },
          }).unwrap();
          const newActionId = res?.createAction?.id;
          if (!newActionId) {
            throw new Error("createAction response missing createAction.id");
          }
          pushUndoAdded(newActionId);
          if (hadOtherSteps) {
            await reorderAction({
              input: { actionId: newActionId, sequenceOrder: REORDER_TO_FIRST },
            }).unwrap();
          }
        }

        if (hasPrependedUndockBeforeLoadMap(actions)) {
          await prependUndockBeforeLoadMap({ missionId: currentMissionId }).unwrap();
        }

        if (mountedRef.current) setSaveStatus("saved");
        addToast({
          type: "success",
          message: t("createEdit.mapSavedSuccess"),
        });
      } catch (e) {
        console.error("handleSaveMission: mission storage step failed after device save", e);
        addToast({
          type: "warning",
          message: t("createEdit.mapStepUpdateFailed"),
        });
        if (mountedRef.current) setSaveStatus("saved");
      } finally {
        setMissionSaveBusy(false);
      }
      return;
    }

    addToast({
      type: "success",
      message: t("createEdit.autoSaveHint"),
    });
    if (mountedRef.current) setSaveStatus("saved");
  };

  // Run directly from create mode: the mission already exists in storage (created
  // on name entry), so promote it out of "creating" chrome and start execution.
  const handleRunMissionFromCreate = () => {
    if (!currentMissionId || isExecuting || missionSaveBusy) return;
    setEditMode(false);
    setInsertionIndex(null);
    setStartHereMode(false);
    setStartHerePending(null);
    dispatch(setIsCreating(false));
    requestMissionStart(currentMissionId, actions);
  };

  const handleBack = async () => {
    if (saveStatus !== 'idle' && saveStatus !== 'saved') {
      setShowDiscardOption(true);
      return;
    }

    if (!isCreating) clearExecutionDisplay();
    dispatch(clearMissionCreation());
    onBack();
  };

  const handleTitleChange = (rawName: string) => {
    const newName = rawName.trim();
    setMissionName(newName);

    if (!newName) {
      setNameError(t("createEdit.nameRequired"));
      return;
    }

    if (isDuplicateMissionName(newName)) {
      setNameError(t("createEdit.nameExists"));
      return;
    }

    setNameError("");

    if (currentMissionId) {
      executeWithRetry(() => updateMission({ id: currentMissionId, input: { name: newName } }).unwrap());
      return;
    }

    executeWithRetry(async () => {
      const payload = await createMission({ input: { name: newName } }).unwrap();
      dispatch(setCurrentMission(payload.createMission.id));
      dispatch(setIsCreating(true));
    });
  };

  const handleNoteChange = (actionId: string, newNote: string) => {
    executeWithRetry(() => updateAction({ id: actionId, input: { note: newNote } }).unwrap());
  };

  const withPlacement = async (createPromise: Promise<any>) => {
    const res = await createPromise;
    const newActionId = res?.createAction?.id;
    const isInserting = editMode && insertionIndex !== null;
    const createdNote = (res?.createAction?.note ?? "") as string;
    const isEndDockCreate = createdNote === MISSION_END_DOCK_NOTE;
    const isEndStop360Create = createdNote === MISSION_END_STOP_360_NOTE;

    if (!newActionId) return res;

    // Mission-end bookends ignore insert mode (+ between steps).
    if (isEndDockCreate) {
      pushUndoAdded(newActionId, false);
      const others = actionsRef.current.filter((a) => a.id !== newActionId);
      const targetOrder =
        others.length === 0
          ? 0
          : Math.max(...others.map((a) => a.sequenceOrder ?? 0)) + 1;
      await reorderAction({
        input: { actionId: newActionId, sequenceOrder: targetOrder },
      }).unwrap();
      return res;
    }

    if (isEndStop360Create) {
      pushUndoAdded(newActionId, false);
      const targetOrder = missionEndStop360SequenceOrder(actionsRef.current, newActionId);
      await reorderAction({
        input: { actionId: newActionId, sequenceOrder: targetOrder },
      }).unwrap();
      return res;
    }

    if (isInserting) {
      pushUndoAdded(newActionId, false);
      await reorderAction({
        input: { actionId: newActionId, sequenceOrder: insertionIndex },
      }).unwrap();
      // Keep inserting after the item just placed (until the user exits insert
      // mode by re-clicking the highlighted "+").
      advanceInsertionIndex(1);
      return res;
    }

    const endPin =
      findMissionEndStop360Action(actionsRef.current) ||
      findMissionEndDockAction(actionsRef.current);
    if (endPin) {
      await reorderAction({
        input: { actionId: newActionId, sequenceOrder: endPin.sequenceOrder },
      }).unwrap();
    }
    pushUndoAdded(newActionId, true);
    return res;
  };

  const handleSaveMapAutoLoadToggle = (actionId: string, enabled: boolean) => {
    if (!currentMissionId) return;
    const action = actions.find((a) => a.id === actionId);
    if (!action || action.actionType !== "SaveMap") return;

    const currentArgs = (action.args ?? {}) as Record<string, unknown>;
    const nextArgs: Record<string, unknown> = { ...currentArgs };
    if (enabled) {
      nextArgs.mission_id = currentMissionId;
    } else {
      nextArgs.mission_id = "";
    }

    executeWithRetry(() =>
      updateAction({
        id: actionId,
        input: {
          name: action.name,
          note: action.note || "",
          args: nextArgs,
        },
      }).unwrap()
    );
  };

  const handleLoadMapFilterChange = (actionId: string, next: LoadMapFilterSelection) => {
    const action = actions.find((a) => a.id === actionId);
    if (!action || action.actionType !== "LoadMap") return;

    const nextArgs: Record<string, unknown> = {
      map_id: next.map_id,
    };
    if (next.variant_bmp_rel_path) {
      nextArgs.variant_bmp_rel_path = next.variant_bmp_rel_path;
    }
    if (next.map_file_id) {
      nextArgs.map_file_id = next.map_file_id;
    }

    executeWithRetry(() =>
      updateAction({
        id: actionId,
        input: {
          name: action.name,
          note: action.note || "",
          args: nextArgs,
        },
      }).unwrap()
    );
  };

  const handleCreateWaypoint = () => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = allocateActionName('NavigateTo', 'Waypoint');

    void runActionCreate(() =>
      withPlacement(createAction({
        input: {
          missionId: currentMissionId,
          actionType: 'NavigateTo',
          name,
          note: '',
          args: { pose: robotPose, map_resource_id: 'map1' },
          sequenceOrder: 0
        }
      }).unwrap())
    );
  };

  const handleNavigateStair = () => {
    setMissionRecording(true);
    setMissionRecordCallback(() => (dialogArgs: any) => {
      if (!currentMissionId || actionCreateBusyRef.current) return;
      const name = allocateActionName('WalkStair', 'Navigate Stair');

      void runActionCreate(() =>
        withPlacement(createAction({
          input: {
            missionId: currentMissionId,
            actionType: 'WalkStair',
            name,
            note: '',
            args: {
              direction: dialogArgs.direction,
              landing: dialogArgs.landing,
              levels: dialogArgs.levels,
              correction_mode: dialogArgs.correction_mode ?? "vision",
            },
            sequenceOrder: 0
          }
        }).unwrap())
      );

      setMissionRecording(false);
      setMissionRecordCallback(null);
      setShowStairDialog(false);
    });
    setShowStairDialog(true);
  };

  const handleTakePhoto = () => {
    setShowTakePhotoDialog(true);
  };

  const createTakePhotoAction = async (photoArgs: {
    camera_name: string;
    ptz_yaw: number;
    ptz_pitch: number;
  }) => {
    if (!currentMissionId) return;
    const name = allocateActionName("TakePhoto", "Take Photo");
    await withPlacement(
      createAction({
        input: {
          missionId: currentMissionId,
          actionType: "TakePhoto",
          name,
          note: "",
          args: photoArgs,
          sequenceOrder: 0,
        },
      }).unwrap()
    );
  };

  const createWaypointThenTakePhoto = async (photoArgs: {
    camera_name: string;
    ptz_yaw: number;
    ptz_pitch: number;
  }) => {
    if (!currentMissionId) return;
    const isEditInsert = editMode && insertionIndex !== null;
    const endDock = findMissionEndDockAction(actionsRef.current);
    const insertAt = isEditInsert
      ? insertionIndex
      : endDock
        ? endDock.sequenceOrder
        : null;
    const scrollIntoView = !isEditInsert;
    const waypointName = allocateActionName("NavigateTo", "Waypoint");
    const photoName = allocateActionName("TakePhoto", "Take Photo");

    const wpRes = await createAction({
      input: {
        missionId: currentMissionId,
        actionType: "NavigateTo",
        name: waypointName,
        note: "",
        args: { pose: robotPose, map_resource_id: "map1" },
        sequenceOrder: 0,
      },
    }).unwrap();
    const wpId = wpRes.createAction.id;
    if (insertAt !== null) {
      await reorderAction({
        input: { actionId: wpId, sequenceOrder: insertAt },
      }).unwrap();
    }
    pushUndoAdded(wpId, scrollIntoView);

    const photoRes = await createAction({
      input: {
        missionId: currentMissionId,
        actionType: "TakePhoto",
        name: photoName,
        note: "",
        args: photoArgs,
        sequenceOrder: 0,
      },
    }).unwrap();
    const photoId = photoRes.createAction.id;
    if (insertAt !== null) {
      // End dock (or following steps) shifted by +2 after waypoint + photo insert.
      await reorderAction({
        input: { actionId: photoId, sequenceOrder: insertAt + 1 },
      }).unwrap();
      if (isEditInsert) {
        // Keep inserting after the photo just placed.
        advanceInsertionIndex(2);
      }
    }
    pushUndoAdded(photoId, scrollIntoView);
  };

  const handleTakePhotoConfirm = (photoArgs: {
    camera_name: string;
    ptz_yaw: number;
    ptz_pitch: number;
  }) => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    setShowTakePhotoDialog(false);

    if (isFrontCameraName(photoArgs.camera_name)) {
      const priorPose = findPriorNavigateToPose(
        visibleActions,
        editMode && insertionIndex !== null ? insertionIndex : null
      );
      if (
        shouldPromptWaypointBeforeFrontPhoto({
          currentPose: robotPose,
          priorWaypointPose: priorPose,
        })
      ) {
        setPendingTakePhotoArgs(photoArgs);
        setShowFrontPhotoWaypointConfirm(true);
        return;
      }
    }

    void runActionCreate(() => createTakePhotoAction(photoArgs));
  };

  const clearPendingFrontPhotoWaypointPrompt = () => {
    setShowFrontPhotoWaypointConfirm(false);
    setPendingTakePhotoArgs(null);
  };

  const handleFrontPhotoWaypointYes = () => {
    if (!pendingTakePhotoArgs || !currentMissionId || actionCreateBusyRef.current) return;
    const photoArgs = pendingTakePhotoArgs;
    clearPendingFrontPhotoWaypointPrompt();
    void runActionCreate(() => createWaypointThenTakePhoto(photoArgs));
  };

  const handleFrontPhotoWaypointNo = () => {
    if (!pendingTakePhotoArgs || !currentMissionId || actionCreateBusyRef.current) return;
    const photoArgs = pendingTakePhotoArgs;
    clearPendingFrontPhotoWaypointPrompt();
    void runActionCreate(() => createTakePhotoAction(photoArgs));
  };

  const handleRecord360 = () => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const command = nextRecord360Command(actions, insertionIndex);
    if (command === "start") {
      void runActionCreate(async () => {
        await createRecord360Action("start", false, "");
      }).then(() => {
        if (!mountedRef.current) return;
        if (!findMissionEndStop360Action(actionsRef.current)) {
          setShowRecord360AppendStop(true);
        }
      });
      return;
    }
    if (findMissionEndStop360Action(actions)) {
      addToast({
        type: "info",
        message: t("createEdit.record360StopAlreadyAtEnd"),
      });
      return;
    }
    record360StopNoteRef.current = "";
    setShowRecord360StopDownload(true);
  };

  const createRecord360Action = async (
    command: "start" | "stop",
    download: boolean,
    note: string
  ) => {
    if (!currentMissionId) return;
    const name = allocateActionName(
      "Record360",
      command === "stop" ? "Stop 360 recording" : "Start 360 recording"
    );
    await withPlacement(
      createAction({
        input: {
          missionId: currentMissionId,
          actionType: "Record360",
          name,
          note,
          args: { command, download },
          sequenceOrder: 0,
        },
      }).unwrap()
    );
  };

  const handleRecord360AppendStopYes = () => {
    setShowRecord360AppendStop(false);
    if (!currentMissionId) return;
    record360StopNoteRef.current = MISSION_END_STOP_360_NOTE;
    setShowRecord360StopDownload(true);
  };

  const handleRecord360StopDownload = (download: boolean) => {
    const note = record360StopNoteRef.current;
    record360StopNoteRef.current = "";
    setShowRecord360StopDownload(false);
    if (!currentMissionId || actionCreateBusyRef.current) return;
    void runActionCreate(async () => {
      await createRecord360Action("stop", download, note);
    });
  };

  const handleRecord360StopDownloadClose = () => {
    record360StopNoteRef.current = "";
    setShowRecord360StopDownload(false);
  };

  const handleWait = () => {
    setShowWaitDialog(true);
  };

  const handleWaitConfirm = (durationSeconds: number) => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = allocateActionName('Wait', 'Wait');

    void runActionCreate(() =>
      withPlacement(createAction({
        input: {
          missionId: currentMissionId,
          actionType: 'Wait',
          name,
          note: '',
          args: {
            duration: durationSeconds
          },
          sequenceOrder: 0
        }
      }).unwrap())
    );

    setShowWaitDialog(false);
  };

  const ensureMissionEndDock = async () => {
    if (!currentMissionId) return;
    const existing = findMissionEndDockAction(actionsRef.current);
    if (existing) {
      if ((existing.note ?? "") !== MISSION_END_DOCK_NOTE) {
        await updateAction({
          id: existing.id,
          input: { note: MISSION_END_DOCK_NOTE },
        }).unwrap();
      }
      const lastIndex = actionsRef.current.length;
      if (existing.sequenceOrder < lastIndex - 1) {
        await reorderAction({
          input: { actionId: existing.id, sequenceOrder: lastIndex },
        }).unwrap();
      }
      return;
    }

    const name = allocateActionName("Docking", "Docking");
    await withPlacement(
      createAction({
        input: {
          missionId: currentMissionId,
          actionType: "Docking",
          name,
          note: MISSION_END_DOCK_NOTE,
          args: { ...DEFAULT_DOCKING_MISSION_ARGS },
          sequenceOrder: 0,
        },
      }).unwrap(),
    );
  };

  const handleDocking = () => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    if (missionHasDockingRequired(actions)) {
      addToast({
        type: "info",
        message: t("createEdit.dockingAlreadyConfigured"),
      });
      return;
    }
    setShowDockingRequiredConfirm(true);
  };

  const handleDockingRequiredYes = async () => {
    setShowDockingRequiredConfirm(false);
    if (!currentMissionId || actionCreateBusyRef.current) return;
    try {
      await runActionCreate(async () => {
        await ensureMissionEndDock();
        await prependUndockBeforeLoadMap({ missionId: currentMissionId }).unwrap();
      });
    } catch (error) {
      console.error("handleDockingRequiredYes:", error);
      addToast({
        type: "error",
        message: t("createEdit.dockingStepFailed"),
      });
    }
  };

  const handleLoadMap = () => {
    loadMapIntentRef.current = "manual";
    setShowLoadMapDialog(true);
  };

  const handleLoadMapConfirm = (selection: MissionStorageLoadMapPickerRow) => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = `${allocateActionName("LoadMap", "Load Map")}: ${selection.label}`;
    const intent = loadMapIntentRef.current;
    const hadUserSteps = visibleActions.length > 0;
    const missionId = currentMissionId;

    void (async () => {
      try {
        await runActionCreate(async () => {
          const res = await createAction({
            input: {
              missionId,
              actionType: "LoadMap",
              name,
              note: "",
              args: {
                map_id: selection.mapId,
                ...(selection.variantBmpRelPath
                  ? { variant_bmp_rel_path: selection.variantBmpRelPath }
                  : {}),
                ...(selection.mapFileId ? { map_file_id: selection.mapFileId } : {}),
              },
              sequenceOrder: 0,
            },
          }).unwrap();

          const newId = res.createAction.id;
          const isInserting = editMode && insertionIndex !== null;
          const endPin =
            findMissionEndStop360Action(actionsRef.current) ||
            findMissionEndDockAction(actionsRef.current);

          if (isInserting) {
            await reorderAction({
              input: { actionId: newId, sequenceOrder: insertionIndex },
            }).unwrap();
            // Keep inserting after the Load Map step just placed.
            advanceInsertionIndex(1);
            pushUndoAdded(newId, false);
          } else if (intent === "initial" && hadUserSteps) {
            await reorderAction({
              input: { actionId: newId, sequenceOrder: REORDER_TO_FIRST },
            }).unwrap();
            pushUndoAdded(newId, true);
          } else if (endPin) {
            await reorderAction({
              input: { actionId: newId, sequenceOrder: endPin.sequenceOrder },
            }).unwrap();
            pushUndoAdded(newId, true);
          } else {
            pushUndoAdded(newId, true);
          }

          if (hasPrependedUndockBeforeLoadMap(actionsRef.current)) {
            await prependUndockBeforeLoadMap({ missionId }).unwrap();
          }
        });
      } catch (e) {
        console.error("handleLoadMapConfirm: failed to create LoadMap action", e);
        return;
      }

      // Only load on the robot when picking the create-flow starting map.
      // Manual / edit append should store the LoadMap step without executing it.
      if (intent !== "initial") return;

      // Outside the create mutex so action buttons unlock after GraphQL, not after ROS load.
      try {
        const stcmRel = stcmRelativePathForLoadMapPickerRow(selection);
        await loadStcmFromRelativePath(stcmRel, {
          variantBmpRelativePath: selection.variantBmpRelPath,
          mapId: selection.mapId,
          mapFileId: selection.mapFileId,
        });
      } catch (e) {
        console.error("handleLoadMapConfirm: failed to load map on ARC", e);
        addToast({
          type: "warning",
          message: t("createEdit.loadMapEditorFailed"),
        });
      }
    })();
    loadMapIntentRef.current = "manual";
  };

  const handleSaveMap = () => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = allocateActionName('SaveMap', 'Save Map');

    void runActionCreate(() =>
      withPlacement(createAction({
        input: {
          missionId: currentMissionId,
          actionType: 'SaveMap',
          name,
          note: '',
          args: {
            map_name: `Map ${Date.now()}`,
            mission_id: currentMissionId
          },
          sequenceOrder: 0
        }
      }).unwrap())
    );
  };

  const handleRelocalization = () => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = allocateActionName('Relocalization', 'Relocalization');

    void runActionCreate(() =>
      withPlacement(createAction({
        input: {
          missionId: currentMissionId,
          actionType: 'Relocalization',
          name,
          note: '',
          args: {},
          sequenceOrder: 0
        }
      }).unwrap())
    );
  };

  const armBuiltinLabels: Record<string, string> = {
    go_zero: "Arm Go Zero",
    ready: "Arm Ready",
    stow: "Arm Stow",
  };

  const handleArm = () => {
    setShowArmMissionDialog(true);
  };

  const createArmPresetAction = (command: ArmPresetCommand) => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const label = armBuiltinLabels[command] ?? `Arm ${command}`;
    const name = allocateActionName("ArmPreset", label);

    void runActionCreate(() =>
      withPlacement(
        createAction({
          input: {
            missionId: currentMissionId,
            actionType: "ArmPreset",
            name,
            note: "",
            args: { command },
            sequenceOrder: 0,
          },
        }).unwrap(),
      ),
    );
  };

  const handleArmMissionSelect = (choice: ArmMissionChoice) => {
    setShowArmMissionDialog(false);
    if (choice.kind === "press_elevator") {
      setShowPressElevatorDialog(true);
      return;
    }
    createArmPresetAction(choice.command);
  };

  const handlePressElevatorButtonConfirm = (pressArgs: PressElevatorButtonArgs) => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = allocateActionName("PressElevatorButton", "Press Elevator");

    void runActionCreate(() =>
      withPlacement(
        createAction({
          input: {
            missionId: currentMissionId,
            actionType: "PressElevatorButton",
            name,
            note: "",
            args: pressArgs,
            sequenceOrder: 0,
          },
        }).unwrap(),
      ),
    );

    setShowPressElevatorDialog(false);
  };

  const handleFailure = () => {
    if (!currentMissionId || actionCreateBusyRef.current) return;
    const name = allocateActionName('Failure', 'Failure');

    void runActionCreate(() =>
      withPlacement(createAction({
        input: {
          missionId: currentMissionId,
          actionType: 'Failure',
          name,
          note: '',
          args: {},
          sequenceOrder: 0
        }
      }).unwrap())
    );
  };

  const handleRemoveAction = (actionId: string) => {
    const action = actions.find((item) => item.id === actionId);
    if (action && action.actionType !== SENTRY_CONFIG_ACTION_TYPE) {
      pushUndoRemoved(action);
    }
    executeWithRetry(() => deleteAction({ id: actionId }).unwrap());
  };

  const handleUndoLastActionChange = async () => {
    const entry = actionUndoStack[actionUndoStack.length - 1];
    if (!entry || !currentMissionId) return;

    setActionUndoStack((prev) => prev.slice(0, -1));
    setSaveStatus("saving");

    try {
      if (entry.type === "added") {
        await deleteAction({ id: entry.actionId }).unwrap();
      } else {
        const res = await createAction({
          input: {
            missionId: entry.snapshot.missionId,
            actionType: entry.snapshot.actionType,
            name: entry.snapshot.name,
            note: entry.snapshot.note,
            args: entry.snapshot.args,
            sequenceOrder: entry.snapshot.sequenceOrder,
          },
        }).unwrap();
        const recreatedId = res.createAction.id;
        if (recreatedId && res.createAction.sequenceOrder !== entry.snapshot.sequenceOrder) {
          await reorderAction({
            input: { actionId: recreatedId, sequenceOrder: entry.snapshot.sequenceOrder },
          }).unwrap();
        }
        setScrollToActionId(recreatedId);
      }
      if (mountedRef.current) setSaveStatus("saved");
    } catch (error) {
      console.error("handleUndoLastActionChange:", error);
      setActionUndoStack((prev) => [...prev, entry]);
      if (mountedRef.current) setSaveStatus("failed");
    }
  };

  const handleSentryModeToggle = (enabled: boolean) => {
    setSentryModeBetweenWaypoints(enabled);
    if (!currentMissionId) return;
    if (sentryConfigAction) {
      executeWithRetry(() =>
        updateAction({
          id: sentryConfigAction.id,
          input: {
            name: SENTRY_CONFIG_ACTION_NAME,
            note: '',
            args: { enabled }
          }
        }).unwrap()
      );
      return;
    }
    if (actionCreateBusyRef.current) return;
    void runActionCreate(() =>
      createAction({
        input: {
          missionId: currentMissionId,
          actionType: SENTRY_CONFIG_ACTION_TYPE,
          name: SENTRY_CONFIG_ACTION_NAME,
          note: '',
          args: { enabled },
          sequenceOrder: 0
        }
      }).unwrap()
    );
  };

  const getStatusText = () => {
    switch (saveStatus) {
      case 'saving': return t("createEdit.saving");
      case 'saved': return t("createEdit.saved");
      case 'retrying': return t("createEdit.notSaved");
      case 'failed': return t("createEdit.failedToSave");
      default: return null;
    }
  };

  const getStatusColor = () => {
    switch (saveStatus) {
      case 'failed': return 'text-warning';
      case 'retrying': return 'text-warning';
      default: return 'text-black60';
    }
  };

  const handleDiscardAndExit = () => {
    // Stop pending retries from updating UI; component will unmount via onBack/PagePush.
    actionCreateBusyRef.current = false;
    setActionCreateBusy(false);
    dispatch(clearMissionCreation());
    onBack();
  };

  const mapStartReady =
    !isCreating ||
    mapStartMode === "new" ||
    mapStartMode === "legacy" ||
    mapStartMode === "current" ||
    (mapStartMode === "existing" && initialExistingMapDone);

  const openInitialExistingMapPicker = () => {
    setMapStartMode("existing");
    setInitialExistingMapDone(false);
    loadMapIntentRef.current = "initial";
    setShowLoadMapDialog(true);
  };

  const selectLegacyMapMode = () => {
    setMapStartMode("legacy");
    setInitialExistingMapDone(true);
  };

  const selectNewMapMode = () => {
    setMapStartMode("new");
    setInitialExistingMapDone(true);
    clearMap();
  };

  const disableMappingIfNeeded = () => {
    if (!controlSocket || connectionState !== ConnectionState.Connected) return;
    try {
      sendCommand(setMapUpdatePublish(false));
    } catch (e) {
      console.warn("CreateUpdateMissionSubpage: failed to disable mapping", e);
    }
  };

  const openCurrentMapNameFlow = () => {
    disableMappingIfNeeded();
    setShowCurrentMapNameDialog(true);
  };

  const handleCurrentMapNameConfirmed = async (normalizedBaseName: string) => {
    if (!currentMissionId) return;
    if (connectionState !== ConnectionState.Connected) {
      addToast({ type: "error", message: t("wizard.currentMapNotConnected") });
      return;
    }
    setCurrentMapSaveBusy(true);
    try {
      await saveMapToDevice(normalizedBaseName, "");
      const mapId = await resolveMapIdAfterDeviceSave(normalizedBaseName);
      if (!mapId) {
        addToast({ type: "warning", message: t("wizard.currentMapIdMissing") });
        return;
      }
      const res = await createAction({
        input: {
          missionId: currentMissionId,
          actionType: "LoadMap",
          name: `Load Map 1: ${normalizedBaseName}`,
          note: PREPENDED_LOAD_MAP_NOTE,
          args: { map_id: mapId },
          sequenceOrder: 0,
        },
      }).unwrap();
      const newId = res.createAction.id;
      if (newId) {
        pushUndoAdded(newId, false);
        await reorderAction({
          input: { actionId: newId, sequenceOrder: REORDER_TO_FIRST },
        }).unwrap();
        if (hasPrependedUndockBeforeLoadMap(actionsRef.current)) {
          await prependUndockBeforeLoadMap({ missionId: currentMissionId }).unwrap();
        }
      }
      setShowCurrentMapNameDialog(false);
      setMapStartMode("current");
      setInitialExistingMapDone(true);
    } catch (e) {
      console.error("handleCurrentMapNameConfirmed:", e);
      addToast({
        type: "error",
        message: e instanceof Error ? e.message : t("wizard.currentMapSaveFailed"),
      });
    } finally {
      setCurrentMapSaveBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-r10 bg-black90">
      {/* Fixed header — Run / Edit / Save controls stay visible while scrolling */}
      <div className="shrink-0 border-b border-black80 px-4 pb-3 pt-4">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex min-w-0 items-center">
            <FunctionalButton
              icon={<ArrowLeftIcon className="w-6" />}
              className="h-6 w-6 shrink-0"
              onClick={handleBack}
            />
            <h2 className="ml-4 truncate text-h3">
              {isCreating
                ? t("createEdit.createTitle")
                : (missionName || t("createEdit.editTitle"))}
            </h2>
            {saveStatus !== 'idle' && (
              <span className={`ml-6 truncate text-h5 ${getStatusColor()}`}>{getStatusText()}</span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {showDiscardOption && saveStatus !== 'saved' && (
              <button
                className="text-h5 text-warning underline transition-colors hover:text-red-400"
                onClick={handleDiscardAndExit}
              >
                {t("createEdit.discardAndExit")}
              </button>
            )}
            {currentMissionId && showActionEditing && (
              <FunctionalButton
                icon={<UndoIcon className="w-4 [&_path]:stroke-current" />}
                className={`h-9 w-9 rounded-r3 hover:bg-black70 ${!canUndoAction ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={!canUndoAction || saveStatus === "saving" || saveStatus === "retrying"}
                onClick={() => void handleUndoLastActionChange()}
              />
            )}
            {currentMissionId && !isThisMissionExecuting && (
              <button
                type="button"
                className={`h-9 rounded-r5 px-4 py-2 text-h5 transition-colors ${editMode ? "bg-primary2 text-white" : "bg-black70 text-black40 hover:bg-black60"}`}
                onClick={() => {
                  const next = !editMode;
                  setEditMode(next);
                  if (!next) setInsertionIndex(null);
                  if (next) {
                    setStartHereMode(false);
                    setStartHerePending(null);
                  }
                }}
              >
                {editMode ? t("createEdit.editOn") : t("createEdit.editOff")}
              </button>
            )}
            {!isCreating && currentMissionId && (
              isThisMissionExecuting ? (
                <>
                  <span className="text-h5 text-black40">{getExecutionStatusText()}</span>
                  {normalizedExecutorStatus === "paused" ? (
                    <FunctionalButton
                      title={t("subpage.continue")}
                      className="h-9 rounded-r5 bg-primary2 px-4 py-2 hover:bg-primary1"
                      onClick={() => resumeMission()}
                    />
                  ) : normalizedExecutorStatus !== "returning" ? (
                    <FunctionalButton
                      title={t("subpage.pause")}
                      className="h-9 rounded-r5 bg-black70 px-4 py-2 hover:bg-black60"
                      onClick={() => pauseMission()}
                    />
                  ) : null}
                  <FunctionalButton
                    title={t("subpage.cancel")}
                    className="h-9 rounded-r5 bg-red-600 px-4 py-2 hover:bg-red-500"
                    onClick={() => cancelMission()}
                  />
                </>
              ) : (
                <>
                  <FunctionalButton
                    title={t("subpage.startHere")}
                    className={`h-9 rounded-r5 px-4 py-2 ${
                      startHereMode
                        ? "bg-primary2 text-white hover:bg-primary1"
                        : "bg-black70 text-black40 hover:bg-black60"
                    }`}
                    onClick={() => {
                      setStartHerePending(null);
                      setStartHereMode((prev) => {
                        const next = !prev;
                        if (next) {
                          setEditMode(false);
                          setInsertionIndex(null);
                        }
                        return next;
                      });
                    }}
                    disabled={isExecuting}
                  />
                  <FunctionalButton
                    title={t("subpage.runMission")}
                    className="h-9 rounded-r5 bg-primary2 px-4 py-2 hover:bg-primary1"
                    onClick={() => {
                      setStartHereMode(false);
                      setStartHerePending(null);
                      requestMissionStart(currentMissionId, actions);
                    }}
                    disabled={isExecuting}
                  />
                </>
              )
            )}
            {isCreating && currentMissionId && mapStartReady && visibleActions.length > 0 && (
              <FunctionalButton
                title={t("subpage.runMission")}
                className="h-9 rounded-r5 bg-primary2 px-4 py-2 text-h5 hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isExecuting || missionSaveBusy}
                onClick={handleRunMissionFromCreate}
              />
            )}
            {isCreating && currentMissionId && mapStartReady && mapStartMode === "new" && (
              <FunctionalButton
                title={missionSaveBusy ? t("createEdit.saving") : t("createEdit.saveMission")}
                className="h-9 rounded-r5 bg-primary2 px-4 py-2 text-h5 hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={missionSaveBusy}
                onClick={() => void handleSaveMission()}
              />
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 pb-3">
        {!isCreating && (
          <>
            {editMode ? (
              <>
                <div
                  className={`mb-4 flex items-center justify-between rounded p-2 transition-all ${isHovered ? "bg-black80 border border-black60" : ""
                    }`}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <input
                    type="text"
                    className="flex-1 bg-transparent outline-none text-h4 transition-colors"
                    value={missionName}
                    placeholder={t("createEdit.namePlaceholder")}
                    onChange={(e) => {
                      setMissionName(e.target.value);
                      if (nameError) {
                        setNameError("");
                      }
                    }}
                    onBlur={(e) => handleTitleChange(e.target.value)}
                  />
                  <PencilIcon className={`w-5 h-5 ml-2 transition-colors ${isHovered ? "text-white" : "text-black60"}`} />
                </div>
                {nameError ? (
                  <p className="mb-4 text-h5 text-warning">{nameError}</p>
                ) : null}
              </>
            ) : (
              <div className="mb-4 rounded bg-black80 p-3">
                <p className="text-h5 text-black60">
                  {t("subpage.created", {
                    datetime: missionData?.mission?.createdAt
                      ? formatLocalDateTime(
                          missionData.mission.createdAt,
                          LOCAL_DATETIME_MEDIUM_SHORT
                        )
                      : "—",
                  })}
                </p>
                <p className="text-h5 text-black60">
                  {t("subpage.actionCount", { count: visibleActions.length })}
                </p>
                <div className="mt-3">
                  <p className="mb-1 text-h5 text-black40">{t("subpage.behaviourProfile")}</p>
                  <select
                    className="w-full rounded-r5 border border-black70 bg-black90 px-3 py-2 text-h5"
                    value={assignedProfileId}
                    onChange={(e) => handleProfileChange(e.target.value)}
                    disabled={isSavingProfile || profiles.length === 0}
                  >
                    <option value="">
                      {profiles.length === 0 ? t("subpage.noProfiles") : t("subpage.selectProfile")}
                    </option>
                    {profiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                  {assignedProfileId && (
                    <p className="mt-1 text-h5 text-black40">
                      {t("subpage.activeProfile", { name: assignedProfileName })}
                    </p>
                  )}
                </div>
                {executorState?.error && (
                  <p className="text-h5 text-red-500 mt-2">
                    {t("subpage.error", { message: executorState.error })}
                  </p>
                )}
              </div>
            )}

            {executionResult && executingMissionId === currentMissionId && (() => {
              const runId = (() => {
                try {
                  return JSON.parse(executionResult.final_state)?.run_id as string | undefined;
                } catch {
                  return undefined;
                }
              })();
              const navigationCanceled = Boolean(executionResult.navigation_canceled);
              const behaviourMessage = behaviourActionFeedback?.message;
              const behaviourRunning = behaviourActionFeedback?.status === "running";
              const behaviourFailed = behaviourActionFeedback?.status === "failed";
              return (
                <div className="mb-4 space-y-2">
                  <div className={`flex items-center justify-between rounded p-3 ${executionResult.success ? "bg-green-900" : navigationCanceled ? "bg-amber-950" : "bg-red-900"}`}>
                    <p className="text-h5">
                      {executionResult.success
                        ? t("subpage.missionCompleted")
                        : navigationCanceled
                          ? t("subpage.navigationCanceled")
                          : t("subpage.missionFailed", { message: executionResult.message })}
                    </p>
                    {executionResult.success && runId && (
                      <Link
                        to={`logs/${runId}`}
                        className="text-h5 text-white hover:text-black40 ml-4 shrink-0"
                      >
                        {t("subpage.viewLog")}
                      </Link>
                    )}
                  </div>
                  {navigationCanceled && (
                    <div
                      className={`rounded p-3 ${
                        behaviourFailed ? "bg-red-950" : behaviourRunning ? "bg-blue-950" : "bg-green-950"
                      }`}
                    >
                      <p className="text-h5 font-semibold">{t("subpage.behaviourReturn")}</p>
                      <p className="text-h5 text-black20">
                        {behaviourMessage
                          || (behaviourRunning
                            ? t("subpage.waitingBehaviourServer")
                            : t("subpage.noRtlFeedback"))}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}

      {currentMissionId && isCreating && mapStartMode === "unset" && (
        <div className="mb-4 rounded-r5 border border-black80 p-3">
            <p className="text-h5 font-semibold">{t("wizard.stepMap")}</p>
            <p className="text-h5 text-black40 mb-3">
              {t("createEdit.mapChoiceHint")}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-r5 bg-black70 px-3 py-2 text-h5 text-black40 transition-colors hover:bg-black60"
                onClick={selectNewMapMode}
              >
                {t("createEdit.newMap")}
              </button>
              <button
                type="button"
                className="rounded-r5 bg-black70 px-3 py-2 text-h5 text-black40 transition-colors hover:bg-black60"
                onClick={openCurrentMapNameFlow}
              >
                {t("createEdit.currentMap")}
              </button>
              <button
                type="button"
                className="rounded-r5 bg-black70 px-3 py-2 text-h5 text-black40 transition-colors hover:bg-black60"
                onClick={openInitialExistingMapPicker}
              >
                {t("createEdit.loadExistingMap")}
              </button>
              <button
                type="button"
                className="rounded-r5 bg-black70 px-3 py-2 text-h5 text-black40 transition-colors hover:bg-black60"
                onClick={selectLegacyMapMode}
              >
                {t("createEdit.noMapLegacy")}
              </button>
            </div>
          </div>
        )}

      {currentMissionId && isCreating && mapStartMode === "legacy" && (
        <div className="mb-4 rounded-r5 border border-black80 p-3">
          <p className="text-h5 font-semibold">{t("createEdit.noMapLegacy")}</p>
          <p className="text-h5 text-black40">
            {t("createEdit.legacyDescription")}
          </p>
        </div>
      )}

        {/* Mission Options — editable when creating or edit mode is on */}
        {showActionEditing && (
          <div className="mb-3 rounded-r5 border border-black80 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-h5 font-semibold">{t("createEdit.sentryModeTitle")}</p>
                <p className="text-h5 text-black40">
                  {t("createEdit.sentryModeHint")}
                </p>
              </div>
              <button
                type="button"
                className={`h-8 min-w-[66px] rounded-r5 px-3 text-h5 transition-colors ${sentryModeBetweenWaypoints ? "bg-primary2 text-white" : "bg-black70 text-black40"
                  }`}
                onClick={() => handleSentryModeToggle(!sentryModeBetweenWaypoints)}
              >
                {sentryModeBetweenWaypoints ? t("createEdit.on") : t("createEdit.off")}
              </button>
            </div>
          </div>
        )}

        {/* Action List */}
        <div>
          <ActionList
            actions={visibleActions}
            onNoteChange={showActionEditing ? handleNoteChange : () => {}}
            onSaveMapAutoLoadToggle={showActionEditing ? handleSaveMapAutoLoadToggle : undefined}
            onLoadMapFilterChange={showActionEditing ? handleLoadMapFilterChange : undefined}
            arcHost={selectedARC?.host ?? arcProxyHost ?? null}
            editMode={editMode}
            insertionIndex={insertionIndex}
            onInsertionIndexChange={setInsertionIndex}
            onRemoveAction={handleRemoveAction}
            scrollToActionId={
              isThisMissionExecuting ? executingActionId : scrollToActionId
            }
            scrollBlock={isThisMissionExecuting ? "start" : "end"}
            startHereMode={startHereMode && !editMode}
            onStartHereSelect={(action) => {
              if (action.actionType !== "NavigateTo") return;
              const index = actions.findIndex((item) => item.id === action.id);
              if (index < 0) return;
              setStartHerePending({ action, index });
            }}
          />
        </div>
      </div>

      <Portal visible={showActionEditing}>
        <div
          className="pointer-events-none fixed bottom-[7.25rem] left-1/2 z-[100] w-[min(28rem,calc(100vw-20rem))] -translate-x-1/2"
          role="toolbar"
          aria-label={t("actions.toolbarAria")}
        >
          <div className="pointer-events-auto rounded-r10 border border-black80 bg-black90/95 p-2 shadow-lg">
            <ActionButtons
              onCreateWaypoint={handleCreateWaypoint}
              onNavigateStair={handleNavigateStair}
              onDocking={handleDocking}
              onTakePhoto={handleTakePhoto}
              onRecord360={handleRecord360}
              onWait={handleWait}
              onLoadMap={handleLoadMap}
              onSaveMap={handleSaveMap}
              onRelocalization={handleRelocalization}
              onArm={handleArm}
              onFailure={handleFailure}
              actionsDisabled={!currentMissionId || !mapStartReady || actionCreateBusy}
            />
          </div>
        </div>
      </Portal>

      {/* Navigate Stairs Dialog */}
      <StairTypeDialog
        visible={showStairDialog}
        onConfirm={handleStairConfirm}
        onClose={() => {
          setShowStairDialog(false);
          setMissionRecording(false);
          setMissionRecordCallback(null);
        }}
      />

      <LoadMapDialog
        visible={showLoadMapDialog}
        arcHost={selectedARC?.host ?? null}
        onClose={() => {
          setShowLoadMapDialog(false);
          if (mapStartMode === "existing" && !initialExistingMapDone) {
            setMapStartMode("unset");
          }
          loadMapIntentRef.current = "manual";
        }}
        onConfirm={(map) => {
          setShowLoadMapDialog(false);
          if (mapStartMode === "existing" && loadMapIntentRef.current === "initial") {
            setInitialExistingMapDone(true);
          }
          handleLoadMapConfirm(map);
        }}
      />
      {/* Take Photo Dialog */}
      <TakePhotoDialog
        visible={showTakePhotoDialog}
        onConfirm={handleTakePhotoConfirm}
        onClose={() => setShowTakePhotoDialog(false)}
      />
      <Record360AppendStopDialog
        visible={showRecord360AppendStop}
        onConfirm={handleRecord360AppendStopYes}
        onDecline={() => setShowRecord360AppendStop(false)}
      />
      <Record360StopDownloadDialog
        visible={showRecord360StopDownload}
        requireAnswer={Boolean(record360StopNoteRef.current)}
        onConfirm={handleRecord360StopDownload}
        onClose={handleRecord360StopDownloadClose}
      />

      <FrontPhotoWaypointConfirmDialog
        visible={showFrontPhotoWaypointConfirm}
        onConfirm={handleFrontPhotoWaypointYes}
        onDecline={handleFrontPhotoWaypointNo}
        onCancel={clearPendingFrontPhotoWaypointPrompt}
      />

      <StartHereConfirmDialog
        visible={startHerePending != null}
        waypointName={startHerePending?.action.name ?? ""}
        onConfirm={() => {
          if (!startHerePending || !currentMissionId) return;
          const startStepIndex = missionStartStepIndexForActionId(
            actions,
            startHerePending.action.id,
          );
          if (startStepIndex == null) {
            // The selected waypoint was removed while its confirmation was open.
            setStartHerePending(null);
            return;
          }
          setStartHerePending(null);
          setStartHereMode(false);
          requestMissionStart(currentMissionId, actions, { startStepIndex });
        }}
        onCancel={() => setStartHerePending(null)}
      />

      <PayloadHealthWarningDialog
        visible={payloadWarningVisible}
        issues={payloadWarningIssues}
        onContinue={continueDespitePayloadWarning}
        onCancel={cancelPayloadWarning}
      />

      <SaveCurrentMapNameDialog
        visible={showCurrentMapNameDialog}
        busy={currentMapSaveBusy}
        onConfirm={(baseName) => void handleCurrentMapNameConfirmed(baseName)}
        onCancel={() => {
          if (currentMapSaveBusy) return;
          setShowCurrentMapNameDialog(false);
        }}
      />

      <WaitDialog
        visible={showWaitDialog}
        onConfirm={handleWaitConfirm}
        onClose={() => setShowWaitDialog(false)}
      />

      <ArmMissionDialog
        visible={showArmMissionDialog}
        onSelect={handleArmMissionSelect}
        onClose={() => setShowArmMissionDialog(false)}
      />

      <PressElevatorButtonDialog
        visible={showPressElevatorDialog}
        onConfirm={handlePressElevatorButtonConfirm}
        onClose={() => setShowPressElevatorDialog(false)}
      />

      <DockingRequiredConfirmDialog
        visible={showDockingRequiredConfirm}
        onConfirm={handleDockingRequiredYes}
        onDecline={() => setShowDockingRequiredConfirm(false)}
      />
    </div>
  );
};

export default CreateUpdateMissionSubpage;
